/**
 * Pure geometry for floor-plan tables: seat positions for every table shape,
 * the crescent outline, seat-count limits, labels and quarter-turn rotation.
 * No fabric.js, no DOM, so every function here is unit tested in isolation.
 *
 * Orientation is never stored. The zones table has no angle column, so the
 * layout is derived from data that is already saved: the box (a wider-than-tall
 * table runs left to right), and for a crescent, which way the stage lies.
 */

export type TableShape = 'round' | 'oval' | 'rect' | 'crescent' | 'rectangular';
export type Direction = 'up' | 'down' | 'left' | 'right';

export interface Box { x: number; y: number; width: number; height: number }
export interface Point { x: number; y: number }

export interface SeatPosition {
    left: number;
    top: number;
    angleRad: number; // rotation of the chair, radians
}

// Chair sizing is shared with floorPlanGeometry (re-exported there).
export const CHAIR_W = 10;
export const CHAIR_H = 8;
export const CHAIR_GAP = 7;

/** Distance from a table edge to the centre of a chair that sits against it. */
const CHAIR_OFFSET = CHAIR_GAP + CHAIR_H / 2;

// ── Seat limits ──────────────────────────────────────────────────────────────
export interface SeatLimits { min: number; max: number; default: number }

export const SEAT_LIMITS: Record<TableShape, SeatLimits> = {
    round:       { min: 1, max: 24, default: 8 },
    oval:        { min: 1, max: 24, default: 8 },
    rect:        { min: 1, max: 24, default: 6 },
    crescent:    { min: 4, max: 12, default: 8 },
    rectangular: { min: 8, max: 16, default: 16 },
};

export function seatLimits(shape: TableShape | undefined): SeatLimits {
    return SEAT_LIMITS[shape ?? 'round'] ?? SEAT_LIMITS.round;
}

/** Whole number inside the shape's range. Non-finite input falls back to the default. */
export function clampSeats(shape: TableShape | undefined, seats: number): number {
    const { min, max, default: dflt } = seatLimits(shape);
    if (!Number.isFinite(seats)) return dflt;
    return Math.min(max, Math.max(min, Math.round(seats)));
}

// ── Default sizes ────────────────────────────────────────────────────────────
const SEAT_PITCH = 24;       // along-table room for one chair on a rectangular table
const RECT_END_PAD = 14;
const RECT_DEPTH = 36;

/** Length of a rectangular table that fits `seats` chairs split over its two long sides. */
export function rectangularLength(seats: number): number {
    const perSide = Math.ceil(clampSeats('rectangular', seats) / 2);
    return perSide * SEAT_PITCH + RECT_END_PAD * 2;
}

export function defaultTableSize(shape: TableShape, seats?: number): { width: number; height: number } {
    switch (shape) {
        case 'crescent':    return { width: 120, height: 60 };
        case 'rectangular': return { width: rectangularLength(seats ?? SEAT_LIMITS.rectangular.default), height: RECT_DEPTH };
        case 'rect':        return { width: 110, height: 60 };
        case 'oval':        return { width: 90, height: 60 };
        default:            return { width: 70, height: 70 };
    }
}

// ── Labels and numbering ─────────────────────────────────────────────────────
/** "12" or "12 (A)" when the table sits in a lettered section. */
export function tableLabel(name: string, sectionLetter?: string | null): string {
    const letter = sectionLetter?.trim();
    return letter ? `${name} (${letter})` : name;
}

/** Next free table number: one above the highest purely numeric table name. */
export function nextTableNumber(tables: ReadonlyArray<{ name: string }>): number {
    let max = 0;
    for (const t of tables) {
        const n = /^\d+$/.test(t.name.trim()) ? Number(t.name.trim()) : 0;
        if (n > max) max = n;
    }
    return max + 1;
}

// ── Direction helpers ────────────────────────────────────────────────────────
const TURN_ORDER: Direction[] = ['up', 'right', 'down', 'left']; // clockwise

/** Clockwise quarter turns (0-3) that carry `from` onto `to`. */
export function quarterTurns(from: Direction, to: Direction): number {
    return (TURN_ORDER.indexOf(to) - TURN_ORDER.indexOf(from) + 4) % 4;
}

export function centreOf(box: Box): Point {
    return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

/**
 * Which way `target` lies from the box, along the box's own axis: a wide box
 * can only face up or down, a tall box only left or right. With no target the
 * result is `fallback` on the right axis.
 */
export function directionToward(box: Box, target: Point | null | undefined, fallback: Direction): Direction {
    const horizontalAxis = box.height > box.width; // tall box -> faces left/right
    const c = centreOf(box);
    if (horizontalAxis) {
        if (!target) return fallback === 'left' || fallback === 'right' ? fallback : 'right';
        return target.x >= c.x ? 'right' : 'left';
    }
    if (!target) return fallback === 'up' || fallback === 'down' ? fallback : 'up';
    return target.y >= c.y ? 'down' : 'up';
}

export function opposite(d: Direction): Direction {
    return d === 'up' ? 'down' : d === 'down' ? 'up' : d === 'left' ? 'right' : 'left';
}

/** Rotate a vector clockwise by n quarter turns (screen coordinates, y down). */
function turn(dx: number, dy: number, n: number): Point {
    switch (((n % 4) + 4) % 4) {
        case 1: return { x: -dy, y: dx };
        case 2: return { x: -dx, y: -dy };
        case 3: return { x: dy, y: -dx };
        default: return { x: dx, y: dy };
    }
}

// ── Crescent ─────────────────────────────────────────────────────────────────
/*
 * Canonical crescent: a half ring whose hollow faces UP (toward the stage) and
 * whose outer arc bulges DOWN. The bounding box is width x height; the outer
 * ellipse has rx = width/2, ry = height and its centre on the top edge. Guests
 * sit on the outer arc, so every chair has a clear view across the hollow.
 * Any other facing is the same shape turned by whole quarter turns about the
 * box centre; for left/right the box is the turned (height x width) one.
 */
function crescentThickness(rx: number, ry: number): number {
    return Math.max(8, Math.min(0.4 * rx, 0.4 * ry));
}

/** The box's own width/height as seen in the canonical (hollow-up) frame. */
export function canonicalSize(box: Box, facing: Direction): { w: number; h: number } {
    return facing === 'left' || facing === 'right'
        ? { w: box.height, h: box.width }
        : { w: box.width, h: box.height };
}

/** Map a point given relative to the canonical frame's centre into world space. */
export function toWorld(box: Box, facing: Direction, dx: number, dy: number): Point {
    const c = centreOf(box);
    const t = turn(dx, dy, quarterTurns('up', facing));
    return { x: c.x + t.x, y: c.y + t.y };
}

/** Closed outline of the crescent in world coordinates. */
export function crescentOutline(box: Box, facing: Direction = 'up', steps = 20): Point[] {
    const { w, h } = canonicalSize(box, facing);
    const rx = w / 2;
    const ry = h;
    const t = crescentThickness(rx, ry);
    const rxi = Math.max(1, rx - t);
    const ryi = Math.max(1, ry - t);
    const originY = -h / 2; // ellipse centre sits on the canonical top edge
    const pts: Point[] = [];
    for (let i = 0; i <= steps; i++) {
        const phi = (Math.PI * i) / steps; // 0 -> pi along the outer arc
        pts.push(toWorld(box, facing, rx * Math.cos(phi), originY + ry * Math.sin(phi)));
    }
    for (let i = steps; i >= 0; i--) {
        const phi = (Math.PI * i) / steps;
        pts.push(toWorld(box, facing, rxi * Math.cos(phi), originY + ryi * Math.sin(phi)));
    }
    return pts;
}

/** Middle of the crescent's solid band at its deepest point: where its number is written. */
export function crescentLabelPoint(box: Box, facing: Direction = 'up'): Point {
    const { w, h } = canonicalSize(box, facing);
    const rx = w / 2;
    const ry = h;
    const t = crescentThickness(rx, ry);
    return toWorld(box, facing, 0, -h / 2 + ry - t / 2);
}

function crescentSeats(box: Box, seats: number, facing: Direction): SeatPosition[] {
    const { w, h } = canonicalSize(box, facing);
    const rx = w / 2;
    const ry = h;
    const originY = -h / 2;
    const turns = quarterTurns('up', facing);
    const out: SeatPosition[] = [];
    for (let i = 0; i < seats; i++) {
        const phi = (Math.PI * (i + 0.5)) / seats;
        const px = rx * Math.cos(phi);
        const py = originY + ry * Math.sin(phi);
        // outward normal of the ellipse at phi
        let nx = Math.cos(phi) / rx;
        let ny = Math.sin(phi) / ry;
        const len = Math.hypot(nx, ny) || 1;
        nx /= len; ny /= len;
        const world = toWorld(box, facing, px + nx * CHAIR_OFFSET, py + ny * CHAIR_OFFSET);
        out.push({
            left: world.x,
            top: world.y,
            angleRad: Math.atan2(ny, nx) + Math.PI / 2 + (turns * Math.PI) / 2,
        });
    }
    return out;
}

// ── Rectangular ──────────────────────────────────────────────────────────────
/** Chairs along both long sides, split evenly (the extra one goes on the first side). */
function rectangularSeats(box: Box, seats: number): SeatPosition[] {
    const horizontal = box.width >= box.height;
    const firstSide = Math.ceil(seats / 2);
    const secondSide = seats - firstSide;
    const out: SeatPosition[] = [];
    const along = (count: number, i: number) => ((i + 0.5) / count);
    for (let i = 0; i < firstSide; i++) {
        out.push(horizontal
            ? { left: box.x + box.width * along(firstSide, i), top: box.y - CHAIR_OFFSET, angleRad: 0 }
            : { left: box.x - CHAIR_OFFSET, top: box.y + box.height * along(firstSide, i), angleRad: -Math.PI / 2 });
    }
    for (let i = 0; i < secondSide; i++) {
        out.push(horizontal
            ? { left: box.x + box.width * along(secondSide, i), top: box.y + box.height + CHAIR_OFFSET, angleRad: Math.PI }
            : { left: box.x + box.width + CHAIR_OFFSET, top: box.y + box.height * along(secondSide, i), angleRad: Math.PI / 2 });
    }
    return out;
}

// ── Round / oval and the legacy rect ─────────────────────────────────────────
function roundSeats(box: Box, seats: number): SeatPosition[] {
    const c = centreOf(box);
    const rx = box.width / 2 + CHAIR_GAP + CHAIR_W / 2;
    const ry = box.height / 2 + CHAIR_GAP + CHAIR_H / 2;
    return Array.from({ length: seats }, (_, i) => {
        const angle = (2 * Math.PI / seats) * i - Math.PI / 2;
        return { left: c.x + rx * Math.cos(angle), top: c.y + ry * Math.sin(angle), angleRad: angle + Math.PI / 2 };
    });
}

// Existing `rect` tables keep their original layout: top and bottom edges only.
function legacyRectSeats(box: Box, seats: number): SeatPosition[] {
    const perSide = Math.ceil(seats / 2);
    const out: SeatPosition[] = [];
    for (let i = 0; i < perSide; i++) {
        const x = box.x + (box.width / (perSide + 1)) * (i + 1);
        out.push({ left: x, top: box.y - CHAIR_GAP - CHAIR_H / 2, angleRad: 0 });
        out.push({ left: x, top: box.y + box.height + CHAIR_GAP + CHAIR_H / 2, angleRad: 0 });
    }
    return out;
}

/**
 * Seat positions for any table shape. `facing` only matters for a crescent
 * (the way its hollow side faces).
 */
export function seatPositions(
    shape: TableShape | undefined,
    box: Box,
    seats: number,
    facing: Direction = 'up',
): SeatPosition[] {
    const n = Math.max(0, Math.floor(seats));
    switch (shape) {
        case 'crescent':    return crescentSeats(box, n, facing);
        case 'rectangular': return rectangularSeats(box, n);
        case 'rect':        return legacyRectSeats(box, n);
        default:            return roundSeats(box, n);
    }
}

// ── Quarter-turn rotation (the only rotation that can be saved) ──────────────
/** Swap width and height about the centre, keeping the centre fixed. */
export function rotateQuarterTurn(box: Box): Box {
    const c = centreOf(box);
    return { x: Math.round(c.x - box.height / 2), y: Math.round(c.y - box.width / 2), width: box.height, height: box.width };
}
